import { AttachFilePanel } from '@/components/create/AttachFilePanel';
import { Box, Button, Tooltip } from '@mui/material';
import {
  MAX_PHOTO_FILE_SIZE_BYTES,
  MESSAGE_PHOTO_FORMAT_UNSUPPORTED,
  MESSAGE_PHOTO_SIZE_EXCEEDED,
  PHOTO_FILE_ACCEPT,
  PHOTO_UPLOAD_TOOLTIP,
  SUPPORTED_PHOTO_FORMATS,
} from '@/utils/validation-utils';
import { PhotoPanelGrid } from '@/components/create/PhotoPanelGrid';
import { SectionCountLabel } from '@/components/common/SectionCountLabel';
import { StagedPhotoDTO } from '@/types/create/StagedPhotoDTO';
import { formatCurrency } from '@/utils/string-utils';
import { useSnackbar } from 'notistack';
import React, { FC, Fragment, ReactElement, useRef } from 'react';

interface Props {
  attachmentCost: number;
  maxPhotoCount: number;
  uploadEnabled?: boolean;
  isPhotoStaging?: boolean;
  photos: StagedPhotoDTO[];
  onAttachPhoto: (file: File) => void;
  onRemovePhoto: (id: string) => void;
}

export const CreateFormAttachFile: FC<Props> = (props: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { attachmentCost, maxPhotoCount, uploadEnabled, isPhotoStaging, photos, onAttachPhoto, onRemovePhoto } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  const handleUploadButtonClick = (): void => {
    inputRef.current?.click();
  };

  const handleUploadChange = (): void => {
    if (inputRef.current?.files?.length) {
      const file: File = inputRef.current.files[0];
      const imageType: string = file.type.split('/')[1];
      if (!SUPPORTED_PHOTO_FORMATS.includes(imageType)) {
        enqueueSnackbar(MESSAGE_PHOTO_FORMAT_UNSUPPORTED, {
          variant: 'error',
        });
      } else if (file.size >= MAX_PHOTO_FILE_SIZE_BYTES) {
        enqueueSnackbar(MESSAGE_PHOTO_SIZE_EXCEEDED, { variant: 'error' });
      } else {
        onAttachPhoto(file);
      }
      // The same photo may be picked again after it was removed, which fires no change event
      // unless the input is emptied first.
      inputRef.current.value = '';
    }
  };

  if (!attachmentCost) {
    return <Fragment />;
  }

  const cells: ReactElement[] = photos.map(
    (photo: StagedPhotoDTO, index: number): ReactElement => (
      <AttachFilePanel
        key={photo.id}
        thumbnail={photo.thumbnail}
        photoCost={attachmentCost}
        label={photos.length > 1 ? `Фото ${index + 1}` : undefined}
        onClear={(): void => {
          onRemovePhoto(photo.id);
        }}
      />
    ),
  );

  if (photos.length < maxPhotoCount) {
    cells.push(
      <Box key={'attach-photo'} aria-label={'attach-photo-cell'}>
        <Tooltip title={PHOTO_UPLOAD_TOOLTIP} disableInteractive={true}>
          <div>
            <Button
              size={'medium'}
              fullWidth={true}
              color={'primary'}
              variant={'outlined'}
              disabled={!uploadEnabled || isPhotoStaging}
              onClick={() => handleUploadButtonClick()}>
              {'Приложить фотографию '}
              <Box component={'span'} sx={{ whiteSpace: 'nowrap' }}>
                {'\u00A0+' + formatCurrency(attachmentCost, 0)}
              </Box>
            </Button>
          </div>
        </Tooltip>
        <input type={'file'} hidden={true} ref={inputRef} accept={PHOTO_FILE_ACCEPT} onChange={handleUploadChange} />
      </Box>,
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <SectionCountLabel
        label={'ФОТОГРАФИИ'}
        count={`${photos.length} из ${maxPhotoCount}`}
        testId={'attach-photo-counter'}
      />
      <PhotoPanelGrid>{cells}</PhotoPanelGrid>
    </Box>
  );
};
